import React from "react";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Text, Image, Box } from "rebass";
import styled from "styled-components";

// -----------------------------------------------------------------------

import { media } from "@sisuwellness/web-components/themes";
import { PrimaryButton } from "@sisuwellness/ui/src/components/Buttons";
import { ModalBody } from "components/NotificationModal";
import { ExtraBreakpointWrapper } from "components/ExtraBreakpointWrapper";
import FlexWithGap from "components/Flex";
import useRudderStack from "hooks/useRudderStack";
import { PORTAL_IMAGE } from "constants/imagePath";
import { useChargebee } from "utilities/chargebee";

import featureList from "./featureList";
import useProgramSubscriptionTracking from "hooks/useProgramSubscriptionTracking";

// -----------------------------------------------------------------------

const StyledImage = styled(Image)`
    left: 0;
    z-index: 1;
    bottom: 0;
    position: absolute;

    ${media.mobile`
        display: none;
    `}
`;

// -----------------------------------------------------------------------

const columnWidth = ["74px", "84px"];

export const ModalContent = ({ close, openModal: openPollingScreen }) => {
    const Chargebee = useChargebee();
    const { search } = useLocation();
    const rudderStack = useRudderStack();
    const { trackSubscriptionInitiated } = useProgramSubscriptionTracking(search, rudderStack);
    const { isCountryUK } = useSelector(state => state.deviceLocation);

    const handleOnUpgrade = () => {
        trackSubscriptionInitiated();
        close();
        Chargebee.openChargebeeCheckout({ callbacks: { openPollingScreen } });
    };

    return (
        <ExtraBreakpointWrapper screen="phablet" p="32px">
            <ModalBody p={["32px 24px", "40px"]} position="relative" data-testid="conversion-modal">
                <FlexWithGap fd="column" jc="center" gap={["4px", "8px"]} mb={["16px", "8px"]}>
                    <FlexWithGap jc="space-between" ai="flex-start">
                        <Text fontSize={["20px", "28px"]} color="hpPrimaryPurple" maxWidth="574px">
                            Unlock more with SiSU Plus™
                        </Text>
                        <Image
                            src={`${PORTAL_IMAGE}/cross.svg`}
                            pt={["4px", "8px"]}
                            height="24px"
                            width="24px"
                            onClick={close}
                            css={{ cursor: "pointer" }}
                        />
                    </FlexWithGap>
                    <Text fontSize={["15px", "17px"]} fontWeight="light" color="hpPrimaryPurple">
                        No lock in contracts, cancel anytime.
                    </Text>
                </FlexWithGap>

                <FlexWithGap ai="center" jc="flex-end" textAlign="center" mb="8px">
                    <FlexWithGap fd="column" width={columnWidth}>
                        <Text fontSize={["16px", "18px"]} color="blackTints900" fontWeight="intermediate">
                            Core
                        </Text>
                        <Text color="hpPurple700" fontSize={["11px", "15px"]}>
                            Free
                        </Text>
                    </FlexWithGap>

                    <FlexWithGap fd="column" width={columnWidth}>
                        <Text fontSize={["16px", "18px"]} color="blackTints900" fontWeight="intermediate">
                            Plus
                        </Text>
                        <Text color="hpPurple700" fontSize={["11px", "15px"]}>
                            $4.99/month
                        </Text>
                    </FlexWithGap>
                </FlexWithGap>

                {featureList(isCountryUK).map(({ featureName, core, premium }, ix) => {
                    return (
                        <React.Fragment key={ix}>
                            <Box as="hr" height="2px" width="100%" bg="greyTints" />
                            <FlexWithGap
                                key={ix}
                                ai="center"
                                jc="space-between"
                                my={["5px", "10px"]}
                                color="blackTints900"
                                data-testid="feature-offering"
                            >
                                <ExtraBreakpointWrapper screen="phablet" width="150px">
                                    <Text width={["135px", "444px"]} fontSize={["13px", "15px"]}>
                                        {featureName}
                                    </Text>
                                </ExtraBreakpointWrapper>
                                <FlexWithGap jc="center" ai="center" width={columnWidth}>
                                    {core && (
                                        <Image
                                            size="24px"
                                            src={`${PORTAL_IMAGE}/premium/green_tick.svg`}
                                            alt="feature-check"
                                        />
                                    )}
                                </FlexWithGap>
                                <FlexWithGap jc="center" ai="center" width={columnWidth}>
                                    {premium && (
                                        <Image
                                            size="24px"
                                            src={`${PORTAL_IMAGE}/premium/green_tick.svg`}
                                            alt="feature-check"
                                        />
                                    )}
                                </FlexWithGap>
                            </FlexWithGap>
                        </React.Fragment>
                    );
                })}
                <Box as="hr" height="2px" width="100%" bg="greyTints" />

                <Text mt="48px" width={["135px", "444px"]} fontSize={["13px", "15px"]}>
                    * BMI, Blood Pressure, Heart Rate, Body Fat, Diabetes Risk
                </Text>

                <FlexWithGap jc="center" ai="center" mt="40px" position="relative" zIndex="2">
                    <PrimaryButton onClick={handleOnUpgrade}>Upgrade to SiSU Plus™</PrimaryButton>
                </FlexWithGap>

                <StyledImage src={`${PORTAL_IMAGE}/premium/modal_footer.svg`} alt="feature-footer" />
            </ModalBody>
        </ExtraBreakpointWrapper>
    );
};

ModalContent.propTypes = {
    close: PropTypes.func,
    openModal: PropTypes.func
};
